/**
 * Shared admin directory / management filter option lists.
 * Empty “all” is omitted — `FilterSelect` supplies the placeholder row.
 */

import type { SelectOption } from "@/types/core";

/**
 * Longest search term the API will accept.
 *
 * Every admin list validator bounds `SearchTerm` at 200 characters and answers 400 past
 * it. Capping the input means the console cannot produce a request its own API rejects.
 */
export const SEARCH_TERM_MAX_LENGTH = 200;

/** Listing status used by admin practice, professional, and user directories. */
export const ADMIN_ENTITY_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" }
];

export const ADMIN_PRACTICE_TYPE_FILTER_OPTIONS: SelectOption[] = [
  { value: "dental", label: "Dental Practice" }
];

export const ADMIN_VERIFICATION_FILTER_OPTIONS: SelectOption[] = [
  { value: "verified", label: "Verified" },
  { value: "pending", label: "Pending review" }
];

/**
 * Account status used by the admin user directory.
 *
 * Separate from {@link ADMIN_ENTITY_STATUS_FILTER_OPTIONS} because these are real
 * `UserStatus` values and those are a practice/professional listing summary. Values must
 * match the status switch in `GetAdminUsersQueryHandler`, which falls through to "no
 * filter" on anything it does not recognise — so "inactive", which that switch has no
 * case for, used to return every user while claiming to narrow the list.
 */
export const ADMIN_USER_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "dormant", label: "Dormant" },
  { value: "suspended", label: "Suspended" },
  { value: "banned", label: "Banned" }
];

/** Values must match `GetAdminUsersQueryHandler` user-type filter (case-insensitive). */
export const ADMIN_USER_ROLE_FILTER_OPTIONS: SelectOption[] = [
  { value: "practice", label: "Practice" },
  { value: "professional", label: "Professional" },
  { value: "admin", label: "Admin" },
  { value: "support", label: "Support" }
];

/**
 * Values must parse as `DeclaredPaymentStatus` server-side — the filter is
 * silently ignored otherwise. "raised" used to sit here and matched nothing,
 * so selecting it showed every dispute including resolved ones.
 */
export const ADMIN_DISPUTE_STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: "disputed", label: "Open" },
  { value: "resolved", label: "Resolved" }
];

export const ADMIN_RATING_FLAGGED_FILTER_OPTIONS: SelectOption[] = [
  { value: "flagged", label: "Flagged Only" },
  { value: "unflagged", label: "Not Flagged" }
];

export function parseRatingFlaggedFilter(
  value: string
): boolean | undefined {
  if (value === "flagged") return true;
  if (value === "unflagged") return false;
  return undefined;
}
